import React, { useEffect, useState } from "react";
import { Ruler, Plus, Loader2, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const FIELDS = [
  { key: "weight_kg",     label: "Weight",     unit: "kg", step: "0.1" },
  { key: "body_fat_pct",  label: "Body fat",   unit: "%",  step: "0.1" },
  { key: "chest_cm",      label: "Chest",      unit: "cm", step: "0.5" },
  { key: "waist_cm",      label: "Waist",      unit: "cm", step: "0.5" },
  { key: "hips_cm",       label: "Hips",       unit: "cm", step: "0.5" },
  { key: "left_arm_cm",   label: "L. arm",     unit: "cm", step: "0.1" },
  { key: "right_arm_cm",  label: "R. arm",     unit: "cm", step: "0.1" },
  { key: "left_thigh_cm", label: "L. thigh",   unit: "cm", step: "0.5" },
  { key: "right_thigh_cm","label": "R. thigh", unit: "cm", step: "0.5" },
  { key: "neck_cm",       label: "Neck",       unit: "cm", step: "0.1" },
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

export default function MeasurementsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const reload = async () => {
    try { setItems(await api.listMeasurements()); } catch {}
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v === "" ? undefined : Number(v) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!Object.values(form).some((v) => v != null)) return;
    setBusy(true);
    try {
      await api.addMeasurement(form);
      setForm({});
      setExpanded(false);
      await reload();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.deleteMeasurement(id);
    await reload();
  };

  const latest = items[items.length - 1];
  const earliest = items[0];
  const delta = (key) => {
    if (!latest || !earliest || latest === earliest) return null;
    const a = earliest[key], b = latest[key];
    if (a == null || b == null) return null;
    return (b - a).toFixed(1);
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="measurements-card">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <Ruler className="h-4 w-4 text-lime-400" /> Body measurements
        </h3>
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid="measurements-add-btn"
          className="text-xs font-semibold flex items-center gap-1 px-2.5 py-1 rounded-full bg-lime-400 text-black hover:bg-lime-300"
        >
          <Plus className="h-3.5 w-3.5" /> {expanded ? "Cancel" : "Add"}
        </button>
      </div>

      {expanded && (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="measurements-form">
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map((f) => (
              <label key={f.key} className="text-xs">
                <span className="text-zinc-400">{f.label} ({f.unit})</span>
                <input
                  type="number" step={f.step}
                  value={form[f.key] ?? ""}
                  onChange={set(f.key)}
                  data-testid={`measurement-${f.key}`}
                  className="mt-1 w-full bg-zinc-950 border border-zinc-800 focus:border-lime-400 rounded-md px-2 py-1.5 text-sm text-white outline-none"
                />
              </label>
            ))}
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="measurements-submit"
            className="w-full rounded-full h-10 bg-lime-400 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save measurement"}
          </button>
        </form>
      )}

      {!expanded && (
        loading ? (
          <div className="mt-4 text-xs text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500">No measurements yet. Log your first one to start tracking progress.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {/* Deltas summary */}
            <div className="grid grid-cols-2 gap-2">
              {["weight_kg", "waist_cm", "chest_cm", "body_fat_pct"].map((k) => {
                const d = delta(k);
                if (d == null) return null;
                const field = FIELDS.find((f) => f.key === k);
                const positive = parseFloat(d) > 0;
                return (
                  <div key={k} className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{field.label} change</div>
                    <div className={`text-base font-semibold ${positive ? "text-amber-400" : "text-lime-400"}`}>
                      {positive ? "+" : ""}{d} {field.unit}
                    </div>
                    <div className="text-[10px] text-zinc-500">{items.length} entries</div>
                  </div>
                );
              })}
            </div>

            {/* History */}
            <details className="text-sm">
              <summary className="cursor-pointer text-xs text-zinc-400 hover:text-zinc-200">History ({items.length})</summary>
              <ul className="mt-2 divide-y divide-zinc-800 border border-zinc-800 rounded-lg overflow-hidden">
                {items.slice().reverse().slice(0, 20).map((m) => (
                  <li key={m.id} className="px-3 py-2 flex items-start justify-between gap-2 bg-zinc-950">
                    <div className="text-xs text-zinc-300">
                      <div className="font-semibold">{fmtDate(m.created_at)}</div>
                      <div className="text-zinc-500 mt-0.5">
                        {FIELDS.filter((f) => m[f.key] != null).map((f) => `${f.label} ${m[f.key]}${f.unit}`).join(" · ") || "—"}
                      </div>
                    </div>
                    <button onClick={() => remove(m.id)} className="text-zinc-500 hover:text-red-400" data-testid={`measurement-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )
      )}
    </div>
  );
}
